// Format date for input fields
export const formatDateForInput = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  return date.toISOString().split('T')[0];
};

// Calculate loan period between two dates
export const calculateLoanPeriod = (startDate, endDate) => {
  if (!startDate || !endDate) return "";
  const start = new Date(startDate);
  const end = new Date(endDate);
  const diffYears = end.getFullYear() - start.getFullYear();
  return diffYears;
};

// Calculate monthly payment based on amount, interest and period
export const calculateMonthlyPayment = (amount, interest, years) => {
  if (!amount || !interest || !years) return "";
  
  const principal = parseFloat(amount);
  const monthlyRate = parseFloat(interest) / 100 / 12;
  const numberOfPayments = parseFloat(years) * 12;
  
  // Monthly payment formula: P * r * (1 + r)^n / ((1 + r)^n - 1)
  const payment = principal * monthlyRate * Math.pow(1 + monthlyRate, numberOfPayments) / 
                (Math.pow(1 + monthlyRate, numberOfPayments) - 1);
  
  return payment.toFixed(2);
};

// Format address for display and submission
export const getFormattedAddress = (street, houseNumber, apartment) => {
  return `${street || ''} ${houseNumber || ''}${apartment ? ', דירה ' + apartment : ''}`.trim();
};

// Calculate weighted interest rate based on tracks
export const calculateWeightedInterest = (tracks) => {
  let totalAmount = 0;
  let weightedInterest = 0;
  
  tracks.forEach(track => {
    if (track.amount && track.interest) {
      const amount = parseFloat(track.amount);
      const interest = parseFloat(track.interest);
      totalAmount += amount;
      weightedInterest += amount * interest;
    }
  });
  
  if (totalAmount === 0) return 0;
  return (weightedInterest / totalAmount).toFixed(2);
};

// Update calculated interest when base or addition changes
export const updateCalculatedInterest = (tracks, index, field, value) => {
  const updatedTracks = [...tracks];
  const track = updatedTracks[index];
  
  if (field === 'current_base_interest' || field === 'addition_rate') {
    // Update the specified field
    track[field] = value;
    
    // Calculate total interest
    const baseInterest = parseFloat(track.current_base_interest || 0);
    const addition = parseFloat(track.addition_rate || 0);
    const totalInterest = (baseInterest + addition).toFixed(2);
    
    // Update the total interest field
    track.interest = totalInterest;
  } else {
    // For other fields, just update normally
    track[field] = value;
  }
  
  return updatedTracks;
};

// Prepare mortgage data for API submission
export const prepareMortgageData = (formData, banks, mortgageData, user) => {
  // Get bank ID from name
  const selectedBank = banks.find(b => b.name === formData.bank);
  if (!selectedBank) {
    throw new Error("יש לבחור בנק תקין");
  }
  
  // Calculate end date based on start date and loan period
  const startDate = new Date(formData.startDate);
  const endDate = new Date(startDate);
  endDate.setFullYear(endDate.getFullYear() + parseInt(formData.loanPeriod || 0));
  
  // Prepare mortgage data for API
  let mortgageUpdateData = {
    bank_id: selectedBank.id,
    amount: parseInt(formData.loanAmount || 0),
    loan_interest: parseFloat(calculateWeightedInterest(formData.mortgageTracks) || 0),
    monthly_payment: parseFloat(formData.monthlyPayment || 0),
    monthly_payment_predict: parseFloat(formData.monthlyPayment || 0) * 1.1, // Estimate 10% increase
    rate_to_shekel: 1, // Default for ILS
    valid_from: startDate.toISOString().split('T')[0],
    valid_to: endDate.toISOString().split('T')[0],
    tracks: []
  };
  
  // If we have existing mortgage data, include the ID
  if (mortgageData && mortgageData.mortgages.length > 0) {
    mortgageUpdateData.id = mortgageData.mortgages[0].id;
  }
  
  // Process each track
  formData.mortgageTracks.forEach(track => {
    if (track.type && track.amount) {
      const trackStartDate = new Date(formData.startDate);
      const trackEndDate = new Date(trackStartDate);
      trackEndDate.setFullYear(trackEndDate.getFullYear() + parseInt(track.period || 0));
      
      const trackData = {
        track_id: parseInt(track.type),
        amount: parseInt(track.amount || 0),
        monthly_payment: parseFloat(track.monthly_payment || 0) || parseFloat(track.amount) / (parseInt(track.period || 30) * 12),
        current_base_interest: parseFloat(track.current_base_interest || 0) || parseFloat(track.interest || 0) - 0.5,
        addition_rate: parseFloat(track.addition_rate || 0) || 0.5,
        current_loan_rate: parseFloat(track.interest || 0),
        valid_from: trackStartDate.toISOString().split('T')[0],
        valid_to: trackEndDate.toISOString().split('T')[0],
      };
      
      // If track has ID (exists already), include it
      if (track.id) {
        trackData.id = track.id;
      }
      
      mortgageUpdateData.tracks.push(trackData);
    }
  });

  return mortgageUpdateData;
};

// Calculate total loan amount from tracks
export const calculateTotalLoanAmount = (tracks) => {
  return tracks.reduce((total, track) => {
    const amount = parseFloat(track.amount || 0);
    return total + amount;
  }, 0).toFixed(2);
};

// Calculate total monthly payment from tracks
export const calculateTotalMonthlyPayment = (tracks) => {
  return tracks.reduce((total, track) => {
    const payment = parseFloat(track.monthly_payment || 0);
    return total + payment;
  }, 0).toFixed(2);
};

// Calculate total loan period (maximum period from tracks)
export const calculateTotalLoanPeriod = (tracks) => {
  return Math.max(...tracks.map(track => parseInt(track.period || 0) || 0));
}; 